/* BeeDTF · Custom neck label designer
   Pick a style -> logo / brand name -> details & care -> quantity per size -> live preview.
   Each size becomes its own cart line (variant = Size / Style) with the details and a print-ready PNG of that label. */
(() => {
  /* ---------- print files go to Google Drive (BeeDTF uploads Apps Script); the order gets the Drive link ---------- */
  const DRIVE = { url: 'https://script.google.com/macros/s/AKfycbwOV5-mEootbo8WzncOWn_1EaEnh751Qp2sMyvIp1jFwB3YH1TxVVSMnjMUrB-Txmo/exec', key: 'bee_8rNHHAr2Vmzxt2jo4zOPZwXS' };
  const SHOPIFY_MAX = 20 * 1024 * 1024, SMALL_MAX = 35 * 1024 * 1024, DRIVE_MAX = 500 * 1024 * 1024;
  const mb = (b) => `${(b / 1048576).toFixed(b < 10485760 ? 1 : 0)} MB`;
  const drivePost = (body) => fetch(DRIVE.url, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify({ key: DRIVE.key, ...body }) }).then((r) => r.json());
  const putFile = (url, file, onp) => new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('PUT', url);
    x.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    x.upload.onprogress = (e) => { if (e.lengthComputable) onp(e.loaded / e.total); };
    x.onload = () => {
      if (x.status === 200 || x.status === 201) { try { resolve(JSON.parse(x.responseText)); } catch (e) { reject(e); } }
      else reject(new Error(`Upload failed (${x.status})`));
    };
    x.onerror = () => reject(new Error('Upload blocked'));
    x.send(file);
  });
  const toB64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  /* one code per visitor (kept 7 days) so all of their uploads land in the same Drive folder */
  const SESSION = (() => {
    try { const s = JSON.parse(localStorage.getItem('bee_session') || 'null'); if (s && s.id && Date.now() - s.t < 7 * 864e5) return s.id; } catch (e) { /* ignore */ }
    const id = 'S' + Math.random().toString(36).slice(2, 8).toUpperCase();
    try { localStorage.setItem('bee_session', JSON.stringify({ id, t: Date.now() })); } catch (e) { /* ignore */ }
    return id;
  })();
  const thumbFile = (file, name) => new Promise((resolve) => {
    if (!/^image\/(png|jpe?g|webp|gif|svg\+xml)$/.test(file.type || '')) { resolve(null); return; }
    const u = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      try {
        const k = Math.min(1, 800 / Math.max(im.naturalWidth || 800, im.naturalHeight || 800));
        const c = document.createElement('canvas'); c.width = Math.max(1, Math.round((im.naturalWidth || 800) * k)); c.height = Math.max(1, Math.round((im.naturalHeight || 800) * k));
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        c.toBlob((b) => { URL.revokeObjectURL(u); resolve(b ? new File([b], `preview-${(name || file.name).replace(/\.[^.]+$/, '')}.png`, { type: 'image/png' }) : null); }, 'image/png');
      } catch (e) { URL.revokeObjectURL(u); resolve(null); }
    };
    im.onerror = () => { URL.revokeObjectURL(u); resolve(null); };
    im.src = u;
  });
  const driveUpload = (file, meta, onChange) => {
    const job = { file, state: 'busy', pct: 0 };
    const set = (s, p) => { job.state = s; job.pct = p || 0; if (onChange) onChange(job); };
    job.promise = (async () => {
      const base = { ref: meta.ref, session: SESSION, name: file.name, type: file.type || 'application/octet-stream', size: file.size, origin: location.origin, product: meta.product || '', label: meta.label || '' };
      set('busy', 0);
      try {
        const init = await drivePost({ action: 'init', ...base });
        if (!init.ok) throw new Error(init.error || 'init');
        const res = await putFile(init.uploadUrl, file, (p) => set('busy', p * 0.99));
        if (!res.id) throw new Error('No file id');
        set('done', 1);
        return `https://drive.google.com/file/d/${res.id}/view`;
      } catch (e1) {
        if (file.size > SMALL_MAX) { set('error'); throw e1; }
        try {
          set('busy', 0.3);
          const r = await drivePost({ action: 'small', ...base, data: await toB64(file) });
          if (!r.ok) throw new Error(r.error || 'small');
          set('done', 1);
          return r.url;
        } catch (e2) { set('error'); throw e2; }
      }
    })();
    job.promise.catch(() => {});
    return job;
  };
  const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  const CARE = [
    ['wash', 'Machine wash cold'],
    ['bleach', 'Do not bleach'],
    ['dry', 'Tumble dry low'],
    ['iron', 'Iron low heat'],
    ['clean', 'Do not dry clean']
  ];
  const FONT = "Arial, Helvetica, sans-serif";
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------- drawing helpers (units: 1/100 inch) ---------- */
  const fitSize = (s, base, maxW, k = 0.68) => {
    const n = Math.max(1, String(s).length);
    return Math.max(5, Math.min(base, maxW / (n * k)));
  };
  const T = (x, y, s, base, maxW, opt = {}) => {
    if (!s) return '';
    const fs = fitSize(s, base, maxW, opt.k);
    return `<text x="${x}" y="${y}" font-family="${FONT}" font-size="${fs.toFixed(2)}" font-weight="${opt.w || 700}" text-anchor="${opt.a || 'middle'}" fill="${opt.fill || '#000'}"${opt.ls ? ` letter-spacing="${opt.ls}"` : ''}>${esc(s)}</text>`;
  };
  const L = (x1, y1, x2, y2, w = 1.6) => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#000" stroke-width="${w}"/>`;

  /* care icons drawn in a 40×40 box */
  const ICON = {
    wash: '<path d="M4 13 9 35H31L36 13" fill="none"/><path d="M3 13c3-4 6-4 8.5 0s5.5 4 8.5 0 6-4 8.5 0 5.5 4 8.5 0" fill="none"/><circle cx="20" cy="26" r="2.6" fill="#000" stroke="none"/>',
    bleach: '<path d="M20 5 37 35H3Z" fill="none"/><path d="M9 9 31 35M31 9 9 35" fill="none"/>',
    dry: '<rect x="5" y="5" width="30" height="30" fill="none"/><circle cx="20" cy="20" r="11" fill="none"/><circle cx="20" cy="20" r="2.6" fill="#000" stroke="none"/>',
    iron: '<path d="M4 33 8.5 18H30c4 0 6 6 6 15Z" fill="none"/><path d="M13 18v-6h18" fill="none"/><circle cx="21" cy="26" r="2.6" fill="#000" stroke="none"/>',
    clean: '<circle cx="20" cy="20" r="14" fill="none"/><path d="M9 9 31 31M31 9 9 31" fill="none"/>'
  };
  const icon = (key, x, y, s) => `<g transform="translate(${x} ${y}) scale(${s / 40})" stroke="#000" stroke-width="3" stroke-linejoin="round" stroke-linecap="round">${ICON[key]}</g>`;
  const iconRow = (keys, cx, y, s, gap) => {
    const w = keys.length * s + (keys.length - 1) * gap;
    return keys.map((k, i) => icon(k, cx - w / 2 + i * (s + gap), y, s)).join('');
  };
  const careText = (m) => m.care.map((k) => (CARE.find((c) => c[0] === k) || [])[1]).filter(Boolean).map((t) => t.toUpperCase());

  /* logo slot: the uploaded logo, or the brand name when there is no logo */
  const logo = (m, cx, cy, w, h, round) => {
    if (m.logo) {
      const clip = round ? `<clipPath id="lc${m.uid}"><circle cx="${cx}" cy="${cy}" r="${Math.min(w, h) / 2}"/></clipPath>` : '';
      return `${clip}<image href="${m.logo}" x="${cx - w / 2}" y="${cy - h / 2}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet"${round ? ` clip-path="url(#lc${m.uid})"` : ''}/>`;
    }
    return brandBlock(m.brand || 'YOUR BRAND', cx, cy, w, h);
  };
  /* brand name as big as it fits, on one or two lines */
  const brandBlock = (name, cx, cy, maxW, maxH) => {
    const words = String(name).split(/\s+/).filter(Boolean);
    let lines = [String(name)];
    const one = Math.min(maxH / 1.08, maxW / (String(name).length * 0.7));
    if (words.length > 1 && String(name).length > 9 && one < maxH * 0.42) {
      let best = null;
      for (let i = 1; i < words.length; i++) {
        const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
        const longest = Math.max(a.length, b.length);
        if (!best || longest < best[2]) best = [a, b, longest];
      }
      lines = [best[0], best[1]];
    }
    const longest = Math.max(...lines.map((l) => l.length));
    const fs = Math.max(6, Math.min(maxH / (lines.length * 1.08), maxW / (longest * 0.7)));
    const top = cy - (lines.length * fs * 1.04) / 2 + fs * 0.82;
    return lines.map((l, i) => `<text x="${cx}" y="${(top + i * fs * 1.04).toFixed(2)}" font-family="${FONT}" font-size="${fs.toFixed(2)}" font-weight="900" text-anchor="middle">${esc(l)}</text>`).join('');
  };
  const brandLine = (m) => (m.logo ? (m.brand || '') : '');

  /* ---------- the 7 styles ---------- */
  const STYLES = [
    { name: 'Style 1', w: 200, h: 300, draw: (m) => {
      const icons = m.care.filter((k) => k !== 'clean').slice(0, 4);
      return `<rect x="2" y="2" width="196" height="296" rx="98" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 100, 70, m.logo ? 80 : 150, m.logo ? 70 : 64, true)
        + T(100, 118, brandLine(m), 15, 150)
        + L(30, 128, 170, 128)
        + T(100, 162, m.size, 34, 120, { w: 900 })
        + L(30, 172, 170, 172)
        + iconRow(icons, 100, 180, 24, 8)
        + careText(m).slice(0, 3).map((t, i) => T(100, 222 + i * 10, t, 8, 150)).join('')
        + T(100, 258, m.fabric, 10, 140)
        + T(100, 270, m.madeIn, 8, 130, { w: 400 })
        + T(100, 284, m.website, 9, 110);
    } },
    { name: 'Style 2', w: 260, h: 280, draw: (m) => {
      const icons = m.care.filter((k) => k !== 'clean').slice(0, 4);
      return `<rect x="2" y="2" width="256" height="276" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 130, 56, m.logo ? 76 : 220, m.logo ? 66 : 70, true)
        + T(130, 102, brandLine(m), 16, 220)
        + L(20, 112, 240, 112)
        + T(130, 146, m.size, 32, 120, { w: 900 })
        + L(20, 156, 240, 156)
        + iconRow(icons, 130, 164, 26, 10)
        + careText(m).slice(0, 4).map((t, i) => T(130, 206 + i * 10, t, 8, 220)).join('')
        + L(20, 246, 240, 246)
        + T(130, 260, m.fabric, 10, 220)
        + T(130, 272, m.madeIn, 8, 220, { w: 400 });
    } },
    { name: 'Style 3', w: 300, h: 220, draw: (m) => {
      const icons = m.care.filter((k) => k !== 'clean').slice(0, 4);
      return `<rect x="2" y="2" width="296" height="216" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 150, 38, m.logo ? 160 : 270, 50, false)
        + T(150, 70, m.madeIn, 10, 240, { w: 400, ls: 1 })
        + L(14, 80, 286, 80)
        + careText(m).slice(0, 4).map((t, i) => T(20, 100 + i * 13, t, 10, 170, { a: 'start' })).join('')
        + T(240, 140, m.size, 46, 100, { w: 900 })
        + L(14, 158, 286, 158)
        + iconRow(icons, 150, 164, 26, 14)
        + T(150, 210, m.fabric, 12, 260);
    } },
    { name: 'Style 4', w: 280, h: 280, draw: (m) => {
      const icons = m.care.filter((k) => k !== 'clean').slice(0, 4);
      return `<circle cx="140" cy="140" r="137" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 140, 66, m.logo ? 66 : 170, m.logo ? 60 : 58, true)
        + T(140, 104, brandLine(m), 16, 190)
        + L(40, 112, 240, 112)
        + T(140, 146, m.size, 32, 110, { w: 900 })
        + iconRow(icons, 140, 156, 24, 10)
        + careText(m).slice(0, 3).map((t, i) => T(140, 196 + i * 10, t, 8, 180)).join('')
        + T(140, 234, m.fabric, 10, 170)
        + T(140, 248, m.madeIn, 8, 150, { w: 400 })
        + T(140, 262, m.website, 9, 120);
    } },
    { name: 'Style 5', w: 300, h: 300, draw: (m) => {
      const keys = m.care.filter((k) => k !== 'clean').slice(0, 4);
      const n = keys.length || 1, step = 260 / n;
      return `<rect x="2" y="2" width="296" height="296" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 150, m.logo ? 50 : 60, m.logo ? 80 : 260, m.logo ? 70 : 74, true)
        + T(150, 106, brandLine(m), 18, 260)
        + T(150, 142, m.size, 32, 110, { w: 900 })
        + T(150, 158, m.fabric, 9, 240)
        + L(14, 166, 286, 166)
        + keys.map((k, i) => {
          const cx = 20 + step * i + step / 2;
          const label = ((CARE.find((c) => c[0] === k) || [])[1] || '').toUpperCase().split(' ');
          const half = Math.ceil(label.length / 2);
          return icon(k, cx - 17, 176, 34) + T(cx, 226, label.slice(0, half).join(' '), 9, step - 6) + T(cx, 237, label.slice(half).join(' '), 9, step - 6);
        }).join('')
        + `<rect x="2" y="262" width="296" height="36" fill="#000"/>`
        + T(150, 286, m.website || m.madeIn, 13, 260, { fill: '#fff' });
    } },
    { name: 'Style 6', w: 280, h: 240, draw: (m) => {
      const keys = m.care.slice(0, 4);
      return `<rect x="2" y="2" width="276" height="236" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 140, 41, m.logo ? 200 : 250, 64, false)
        + L(2, 80, 278, 80, 3)
        + `<rect x="2" y="80" width="80" height="120" fill="none" stroke="#000" stroke-width="3"/>`
        + T(42, 146, m.size, 36, 66, { w: 900 })
        + T(42, 176, m.fabric, 8, 70, { w: 400 })
        + T(42, 188, m.madeIn, 7, 70, { w: 400 })
        + keys.map((k, i) => icon(k, 96, 88 + i * 28, 22) + T(126, 104 + i * 28, ((CARE.find((c) => c[0] === k) || [])[1] || '').toUpperCase(), 10, 145, { a: 'start' })).join('')
        + `<rect x="2" y="200" width="276" height="38" fill="#000"/>`
        + T(140, 225, m.website, 13, 250, { fill: '#fff' });
    } },
    { name: 'Style 7', w: 300, h: 200, draw: (m) => {
      const icons = m.care.filter((k) => k !== 'clean').slice(0, 3);
      const notes = [m.fabric, m.madeIn, careText(m)[0]].filter(Boolean);
      return `<rect x="2" y="2" width="296" height="196" fill="#fff" stroke="#000" stroke-width="3"/>`
        + logo(m, 106, 50, 184, 76, false)
        + `<rect x="210" y="2" width="88" height="96" fill="#000"/>`
        + T(254, 66, m.size, 36, 76, { w: 900, fill: '#fff' })
        + L(2, 98, 298, 98, 3)
        + notes.map((t, i) => T(14, 120 + i * 18, t.toUpperCase(), 12, 130, { a: 'start' })).join('')
        + L(150, 98, 150, 168, 3)
        + iconRow(icons, 224, 116, 30, 10)
        + L(2, 168, 298, 168, 3)
        + T(150, 190, m.website, 13, 270);
    } }
  ];

  const labelSVG = (m, styleIdx, opts = {}) => {
    const st = STYLES[styleIdx];
    const pad = opts.pad == null ? 4 : opts.pad;
    const w = st.w + pad * 2, h = st.h + pad * 2;
    const size = opts.px ? ` width="${opts.px * w / 100}" height="${opts.px * h / 100}"` : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="${-pad} ${-pad} ${w} ${h}"${size} role="img" aria-label="${esc(st.name)} neck label preview">${st.draw(m)}</svg>`;
  };

  /* print-ready PNG at 300 DPI (white turns transparent so only the black ink prints) */
  const toPNG = (m, styleIdx) => new Promise((resolve) => {
    const st = STYLES[styleIdx];
    const dpi = 300;
    const svg = labelSVG(m, styleIdx, { pad: 0, px: dpi }).replace(/fill="#fff"/g, 'fill="none"');
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = Math.round(st.w * dpi / 100); c.height = Math.round(st.h * dpi / 100);
        const g = c.getContext('2d');
        g.drawImage(img, 0, 0, c.width, c.height);
        c.toBlob((b) => resolve(b), 'image/png');
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  });

  const init = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const data = JSON.parse(root.querySelector('[data-json]').textContent);
    const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' });
    const fmt = (c) => money.format(c / 100);
    const $ = (s) => root.querySelector(s);
    const addUrl = root.dataset.addUrl || '/cart/add';
    const cartUrl = root.dataset.cartUrl || '/cart';

    const variantFor = (size, style) => data.variants.find((v) => v.option1 === size && v.option2 === STYLES[style].name);
    const sizes = SIZES.filter((s) => data.variants.some((v) => v.option1 === s));

    const m = {
      uid: Math.random().toString(36).slice(2, 7),
      brand: '', logo: null, logoFile: null,
      size: 'M', fabric: '100% COTTON', madeIn: 'MADE IN USA', website: '',
      care: ['wash', 'bleach', 'dry', 'iron']
    };
    let style = 1;
    const qty = Object.fromEntries(sizes.map((s) => [s, 0]));

    const stage = $('[data-stage]');
    const stageCap = $('[data-stage-cap]');
    const styleWrap = $('[data-styles]');
    const sizeTabs = $('[data-size-tabs]');
    const qtyWrap = $('[data-qty]');
    const totalEl = $('[data-total]');
    const totalMeta = $('[data-total-meta]');
    const addBtn = $('[data-add]');
    const addLabel = $('[data-add-label]');
    const addText = addLabel.textContent;
    if (!document.getElementById('bee-buzz-css')) { const st = document.createElement('style'); st.id = 'bee-buzz-css'; st.textContent = `.bee-buzz{display:inline-block;animation:bee-buzz .18s linear infinite}@keyframes bee-buzz{0%,100%{transform:translate(0,0) rotate(0)}25%{transform:translate(.7px,-.6px) rotate(-2deg)}50%{transform:translate(-.6px,.5px) rotate(1.5deg)}75%{transform:translate(.5px,.7px) rotate(-1deg)}}@media (prefers-reduced-motion:reduce){.bee-buzz{animation:none}}`; document.head.appendChild(st); }
    const err = $('[data-err]');
    const logoIn = $('[data-logo]');
    const logoName = $('[data-logo-name]');
    const logoClear = $('[data-logo-clear]');

    /* style thumbnails */
    const sample = { ...m, uid: 'th', brand: m.brand || 'BRAND', size: 'M', website: m.website || 'YOURSITE.COM' };
    const drawStyles = () => {
      const s = { ...sample, logo: m.logo, brand: m.brand || 'BRAND', website: m.website || 'YOURSITE.COM', fabric: m.fabric, madeIn: m.madeIn, care: m.care };
      styleWrap.innerHTML = STYLES.map((st, i) => `<button type="button" class="bee-nl__style" data-style="${i}" aria-pressed="${i === style}" aria-label="${esc(st.name)}">${labelSVG({ ...s, uid: 'th' + i }, i)}<span>${esc(st.name)}</span></button>`).join('');
    };
    sizeTabs.innerHTML = sizes.map((s) => `<button type="button" data-tab="${s}" aria-pressed="${s === m.size}">${s}</button>`).join('');
    qtyWrap.innerHTML = sizes.map((s) => `<label class="bee-nl__q"><span>${s}</span><input type="number" min="0" max="9999" step="1" inputmode="numeric" value="0" data-q="${s}" aria-label="Quantity for size ${s}"></label>`).join('');

    const render = (thumbs) => {
      const st = STYLES[style];
      stage.innerHTML = labelSVG(m, style);
      stageCap.textContent = `${st.name} · ${(st.w / 100).toFixed(1)}″ × ${(st.h / 100).toFixed(1)}″ · size ${m.size}`;
      if (thumbs) drawStyles();
      else styleWrap.querySelectorAll('[data-style]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.style) === style)));
      sizeTabs.querySelectorAll('[data-tab]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.tab === m.size));
        b.classList.toggle('has-qty', qty[b.dataset.tab] > 0);
      });
      const v0 = variantFor(sizes[0], style);
      const unit = v0 ? v0.price : 0;
      const n = sizes.reduce((a, s) => a + qty[s], 0);
      const total = sizes.reduce((a, s) => a + qty[s] * ((variantFor(s, style) || {}).price || 0), 0);
      totalEl.textContent = fmt(total);
      const used = sizes.filter((s) => qty[s] > 0);
      totalMeta.textContent = n ? `${n} label${n === 1 ? '' : 's'} · ${used.length} size${used.length === 1 ? '' : 's'} · ${fmt(unit)} each` : `${fmt(unit)} per label · enter quantities below`;
    };

    /* events */
    styleWrap.addEventListener('click', (e) => {
      const b = e.target.closest('[data-style]');
      if (!b) return;
      style = Number(b.dataset.style);
      render(false);
    });
    sizeTabs.addEventListener('click', (e) => {
      const b = e.target.closest('[data-tab]');
      if (!b) return;
      m.size = b.dataset.tab;
      render(false);
    });
    root.querySelectorAll('[data-field]').forEach((el) => el.addEventListener('input', () => {
      m[el.dataset.field] = el.value.trim().toUpperCase();
      render(true);
    }));
    root.querySelectorAll('[data-care]').forEach((el) => el.addEventListener('change', () => {
      m.care = CARE.map((c) => c[0]).filter((k) => root.querySelector(`[data-care="${k}"]`).checked);
      render(true);
    }));
    qtyWrap.addEventListener('input', (e) => {
      const el = e.target.closest('[data-q]');
      if (!el) return;
      qty[el.dataset.q] = Math.min(9999, Math.max(0, parseInt(el.value, 10) || 0));
      if (qty[el.dataset.q] > 0) m.size = el.dataset.q;
      err.hidden = true;
      render(false);
    });
    qtyWrap.addEventListener('focusin', (e) => {
      const el = e.target.closest('[data-q]');
      if (el && el.value === '0') el.select();
    });
    root.querySelectorAll('[data-quick]').forEach((b) => b.addEventListener('click', () => {
      const n = parseInt(b.dataset.quick, 10);
      const set = b.dataset.set ? b.dataset.set.split(',') : sizes;
      sizes.forEach((s) => { qty[s] = set.includes(s) ? n : qty[s]; });
      qtyWrap.querySelectorAll('[data-q]').forEach((el) => { el.value = qty[el.dataset.q]; });
      err.hidden = true;
      render(false);
    }));
    logoIn.addEventListener('change', () => {
      const f = logoIn.files && logoIn.files[0];
      if (!f) return;
      if (f.size > (DRIVE.url ? DRIVE_MAX : SHOPIFY_MAX)) { logoName.textContent = `This file is ${mb(f.size)}. Please upload a smaller logo.`; logoIn.value = ''; return; }
      const job = DRIVE.url ? driveUpload(f, { ref: m.uid, product: 'Neck labels', label: 'logo' }) : null;   /* full file goes to Drive */
      const use = (src) => {
        m.logo = src;
        m.logoFile = f;
        m.logoJob = job;
        logoName.textContent = f.name;
        logoClear.hidden = false;
        root.classList.add('has-logo');
        render(true);
      };
      if (/svg/i.test(f.type) || /\.svg$/i.test(f.name)) {
        const r = new FileReader();
        r.onload = () => use(String(r.result));
        r.readAsDataURL(f);
        return;
      }
      /* big photos/PNGs: keep a light copy (max 1400 px) for the label preview, the print still uses the Drive original */
      logoName.textContent = `Reading ${f.name}…`;
      const u = URL.createObjectURL(f);
      const im = new Image();
      im.onload = () => {
        const k = Math.min(1, 1400 / Math.max(im.naturalWidth || 1, im.naturalHeight || 1));
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(im.naturalWidth * k)); c.height = Math.max(1, Math.round(im.naturalHeight * k));
        c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
        URL.revokeObjectURL(u);
        use(c.toDataURL('image/png'));
      };
      im.onerror = () => { URL.revokeObjectURL(u); logoName.textContent = 'We could not read this image. Please use PNG, JPG or SVG.'; logoIn.value = ''; };
      im.src = u;
    });
    logoClear.addEventListener('click', () => {
      m.logo = null; m.logoFile = null; m.logoJob = null; logoIn.value = '';
      logoName.textContent = 'PNG, JPG or SVG. Black-and-white logos print best.';
      logoClear.hidden = true;
      root.classList.remove('has-logo');
      render(true);
    });

    /* add to cart: one line per size */
    $('[data-form]').addEventListener('submit', async (e) => {
      e.preventDefault();
      err.hidden = true;
      const used = sizes.filter((s) => qty[s] > 0);
      if (!used.length) { err.textContent = 'Enter how many labels you need for at least one size.'; err.hidden = false; qtyWrap.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      if (!m.logo && !m.brand) { err.textContent = 'Upload your logo or type your brand name.'; err.hidden = false; $('[data-field="brand"]').focus(); return; }
      addBtn.classList.add('is-busy');
      const keep = m.size;
      try {
        for (let i = 0; i < used.length; i++) {
          const s = used[i];
          addLabel.innerHTML = `<span class="bee-buzz">Bzzz…</span> ${used.length > 1 ? `adding ${i + 1} of ${used.length}` : 'adding to your cart'}`;
          const v = variantFor(s, style);
          if (!v) throw new Error(`Size ${s} is not available in ${STYLES[style].name}.`);
          m.size = s;
          const png = await toPNG(m, style);
          const fd = new FormData();
          fd.append('id', v.id);
          fd.append('quantity', qty[s]);
          if (png) fd.append('properties[Label]', new File([png], `neck-label-${STYLES[style].name.replace(' ', '')}-${s}.png`, { type: 'image/png' }));
          if (m.brand) fd.append('properties[Brand name]', m.brand);
          if (m.logoFile) {
            let logoLink = null;
            if (m.logoJob) { try { logoLink = await m.logoJob.promise; } catch (er) { logoLink = null; } }
            if (logoLink) { fd.append('properties[Logo]', m.logoFile.name); fd.append('properties[_Logo file]', logoLink); }
            else if (m.logoFile.size > SHOPIFY_MAX) throw new Error(`Your logo (${mb(m.logoFile.size)}) could not be uploaded. Please check your connection and try again.`);
            else fd.append('properties[Logo]', m.logoFile);
          }
          if (png && DRIVE.url) {
            try {
              const pf = new File([png], `neck-label-${STYLES[style].name.replace(' ', '')}-${s}.png`, { type: 'image/png' });
              fd.append('properties[_Print file]', await driveUpload(pf, { ref: m.uid, product: 'Neck labels', label: `${STYLES[style].name} ${s}` }).promise);
            } catch (er) { /* the label PNG is still attached on Shopify */ }
          }
          if (m.fabric) fd.append('properties[Fabric]', m.fabric);
          if (m.madeIn) fd.append('properties[Made in]', m.madeIn);
          if (m.website) fd.append('properties[Website]', m.website);
          const care = careText(m);
          if (care.length) fd.append('properties[Care]', care.join(' · '));
          fd.append('properties[_Label size]', `${(STYLES[style].w / 100).toFixed(1)}″ × ${(STYLES[style].h / 100).toFixed(1)}″`);
          const res = await fetch(`${addUrl}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
          if (!res.ok) {
            const j = await res.json().catch(() => ({}));
            throw new Error(j.description || `Size ${s} could not be added to the cart.`);
          }
        }
        location.href = cartUrl;
      } catch (ex) {
        m.size = keep;
        render(false);
        err.textContent = ex.message;
        err.hidden = false;
        addBtn.classList.remove('is-busy');
        addLabel.textContent = addText;
      }
    });

    render(true);
  };

  const boot = () => document.querySelectorAll('.bee-nl').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
